/** Agent booking summary: one handling agent's active portfolio by state, lane, carrier. */
import { formatDimensionValue } from "@/lib/formatDimensionValue";
import type { AgentBookingSummaryPayload, AgentGroupEntry } from "@/types/feed";

import {
  agentBookingsHref,
  carrierBookingsHref,
  laneBookingsHref,
  stateBookingsHref,
} from "../../feedLinks";
import { compareScale, formatNum } from "../formatInsight";
import { explainerFor } from "../insightCopy";
import {
  ChangeRow,
  CtaLink,
  EntitySection,
  InsightShell,
  MetricGrid,
  type MetricItem,
} from "../primitives";

function GroupSection({
  title,
  entries,
  hrefFor,
  lane = false,
  formatName,
}: {
  title: string;
  entries: AgentGroupEntry[];
  hrefFor?: (label: string) => string | null;
  lane?: boolean;
  formatName?: (label: string) => string;
}) {
  const max = compareScale(entries.map((g) => g.count));
  return (
    <EntitySection title={title}>
      {entries.map((g) => (
        <ChangeRow
          key={g.label}
          name={formatName ? formatName(g.label) : g.label}
          href={hrefFor ? hrefFor(g.label) : null}
          lane={lane}
          primary={String(g.count)}
          subtitle={g.teu > 0 ? `${formatNum(g.teu)} TEU` : undefined}
          bar={{ type: "share", value: g.count, max }}
        />
      ))}
    </EntitySection>
  );
}

export function AgentBookingSummaryView({
  payload,
}: {
  payload: AgentBookingSummaryPayload;
}) {
  const m = payload.metrics;
  const metricItems: MetricItem[] = m
    ? [
        { label: "Active", value: String(m.active_count) },
        { label: "Active TEU", value: `${formatNum(m.active_teu)} TEU` },
      ]
    : [];

  return (
    <InsightShell explainer={explainerFor(payload as unknown as Record<string, unknown>)}>
      {metricItems.length > 0 && <MetricGrid items={metricItems} />}
      {payload.by_state && payload.by_state.length > 0 && (
        <GroupSection
          title="By state"
          entries={payload.by_state}
          hrefFor={stateBookingsHref}
          formatName={(label) => formatDimensionValue("state", label) || label}
        />
      )}
      {payload.top_lanes && payload.top_lanes.length > 0 && (
        <GroupSection
          title="Top lanes"
          entries={payload.top_lanes}
          hrefFor={laneBookingsHref}
          lane
        />
      )}
      {payload.top_carriers && payload.top_carriers.length > 0 && (
        <GroupSection
          title="Top carriers"
          entries={payload.top_carriers}
          hrefFor={carrierBookingsHref}
        />
      )}
      {payload.agent && (
        <CtaLink to={agentBookingsHref(payload.agent)}>View agent bookings</CtaLink>
      )}
    </InsightShell>
  );
}
